import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EnduserSearchCombobox, type Enduser } from "@tellescope/contacts";

export interface EnduserIdFilterInputProps {
  /** The currently selected enduser id (or a raw, freeform-entered patient id). */
  value?: string;
  onChange: (value: string | undefined) => void;
  className?: string;
}

/**
 * Adapts {@link EnduserSearchCombobox} to the tickets filter's single-raw-id value: a picked patient
 * (or freeform-entered patient ID) is reported back as a bare id string.
 */
export const EnduserIdFilterInput = ({ value, onChange, className }: EnduserIdFilterInputProps) => {
  const { t } = useTranslation("contacts");
  const [selectedEnduser, setSelectedEnduser] = useState<Enduser | undefined>();

  const isResolved = !!value && selectedEnduser?.id === value;
  const endusers = useMemo(
    () => (isResolved && selectedEnduser ? [selectedEnduser] : []),
    [isResolved, selectedEnduser]
  );
  const freeformIds = useMemo(() => (value && !isResolved ? [value] : []), [value, isResolved]);

  const pendingSelectionRef = useRef<{ enduser?: Enduser; freeformId?: string }>({});

  const reportSelection = useCallback(
    (pending: { enduser?: Enduser; freeformId?: string }) => {
      pendingSelectionRef.current = pending;
      setSelectedEnduser(pending.enduser);
      onChange(pending.enduser?.id ?? pending.freeformId);
    },
    [onChange]
  );

  const handleChange = useCallback(
    (next: Enduser[]) => {
      const [picked] = next;
      reportSelection({ enduser: picked, freeformId: picked ? undefined : pendingSelectionRef.current.freeformId });
    },
    [reportSelection]
  );

  const handleFreeformIdsChange = useCallback(
    (ids: string[]) => {
      const [freeformId] = ids;
      reportSelection({ enduser: freeformId ? undefined : pendingSelectionRef.current.enduser, freeformId });
    },
    [reportSelection]
  );

  return (
    <EnduserSearchCombobox
      value={endusers}
      onChange={handleChange}
      freeformIds={freeformIds}
      onFreeformIdsChange={handleFreeformIdsChange}
      placeholder={t("Value")}
      searchPlaceholder={t("Search patients...")}
      emptyText={t("No patients found")}
      debounceMs={400}
      className={className}
    />
  );
};
